import { useEffect } from "react";
import { CheckCircle2, ArrowRight, ShieldCheck } from "lucide-react";
import { haptic, HAPTIC_SUCCESS } from "@/lib/haptics";

// Premium full-screen success animation shown after any successful payment.
// Fires haptic feedback on mount. Variants: access | payment | claim | drone.
const VARIANTS = {
  access: { title: "Access Granted", subtitle: "Welcome to Crash Ready — your attorney portal is now active.", cta: "Enter Attorney Portal" },
  payment: { title: "Payment Confirmed", subtitle: "Your payment was processed successfully.", cta: "Continue" },
  claim: { title: "Case Claimed", subtitle: "Payment confirmed — the client's contact details are unlocked.", cta: "Open Case" },
  drone: { title: "Drone Deployment Authorized", subtitle: "Payment confirmed — your deployment is authorized.", cta: "Continue" },
};

export default function PaymentSuccess({ variant = "payment", amount, onContinue }) {
  const v = VARIANTS[variant] || VARIANTS.payment;
  useEffect(() => { haptic(HAPTIC_SUCCESS); }, []);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-navy overflow-hidden" data-testid="payment-success">
      {/* animated radial glow */}
      <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 50% 40%, rgba(56,189,248,0.35), transparent 60%)" }} />
      <div className="relative text-center px-6 cr-fade-up">
        <div className="mx-auto mb-6 relative h-28 w-28">
          <span className="absolute inset-0 rounded-full bg-emerald-400/20 animate-ping" />
          <span className="absolute inset-2 rounded-full bg-emerald-400/10" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-24 w-24 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center shadow-[0_0_60px_rgba(16,185,129,0.6)]">
              <CheckCircle2 className="w-12 h-12 text-white" strokeWidth={2.5} />
            </div>
          </div>
        </div>
        <div className="inline-flex items-center gap-1.5 text-cyan-300 text-xs font-semibold uppercase tracking-[0.2em] mb-3">
          <ShieldCheck className="w-3.5 h-3.5" /> Crash Ready
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white" data-testid="payment-success-title">{v.title}</h1>
        <p className="mt-3 text-slate-300 max-w-md mx-auto">{v.subtitle}</p>
        {amount != null && <p className="mt-2 text-cyan-300 font-semibold">${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} charged</p>}
        <button onClick={onContinue} data-testid="payment-success-continue"
          className="mt-8 inline-flex items-center gap-2 bg-white text-navy px-7 py-3.5 rounded-full font-semibold hover:-translate-y-0.5 transition-all active:scale-95">
          {v.cta} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
